import { StandardEvents } from '@shopify/events';

/* ---------- Cart count + drawer trigger ---------- */
const setCount = (count) => {
  for (const el of document.querySelectorAll('[data-lc-cart-count]')) {
    el.textContent = count > 99 ? '99+' : String(count);
    el.hidden = count === 0;
  }
};

const refreshCount = () =>
  fetch(`${window.Theme?.routes?.cart_url ?? '/cart'}.js`)
    .then((response) => response.json())
    .then((cart) => setCount(cart.item_count))
    .catch(() => {});

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  event.promise
    ?.then(({ cart, detail }) => setCount(cart?.totalQuantity ?? detail?.itemCount ?? 0))
    .catch(() => {});
});

document.addEventListener('click', (event) => {
  const trigger = event.target instanceof Element ? event.target.closest('[data-lc-cart]') : null;
  if (!trigger) return;
  const drawer = document.getElementById('cart-drawer');
  if (drawer && typeof drawer.open === 'function') {
    event.preventDefault();
    drawer.open();
  }
});

window.addEventListener('pageshow', (event) => {
  if (event.persisted) refreshCount();
});

/* ---------- Shared scrim ---------- */
const header = document.querySelector('[data-lc-header]');
const scrim = header?.querySelector('[data-lc-scrim]');
const openLayers = new Set();
const syncScrim = () => {
  if (!scrim) return;
  if (openLayers.size > 0) scrim.style.top = `${Math.max(0, header.getBoundingClientRect().bottom)}px`;
  scrim.hidden = openLayers.size === 0;
  header.classList.toggle('lc-header--layer-open', openLayers.size > 0);
};

/* ---------- Mega navigation ---------- */
const desktop = window.matchMedia('(min-width: 990px)');
const megaItems = [...document.querySelectorAll('[data-lc-mega]')];
let openMega = null;
let closeTimer = 0;
let openTimer = 0;

const closeMega = (item = openMega, restoreFocus = false) => {
  if (!item) return;
  const toggle = item.querySelector('.lc-mega-toggle');
  const panel = item.querySelector('[data-lc-mega-panel]');
  toggle.setAttribute('aria-expanded', 'false');
  item.classList.remove('is-open');
  panel.hidden = true;
  if (openMega === item) openMega = null;
  openLayers.delete('mega');
  syncScrim();
  if (restoreFocus) toggle.focus();
};

const openMegaItem = (item) => {
  if (openMega && openMega !== item) closeMega(openMega);
  const toggle = item.querySelector('.lc-mega-toggle');
  const panel = item.querySelector('[data-lc-mega-panel]');
  panel.hidden = false;
  item.classList.add('is-open');
  toggle.setAttribute('aria-expanded', 'true');
  openMega = item;
  openLayers.add('mega');
  syncScrim();
};

for (const item of megaItems) {
  const toggle = item.querySelector('.lc-mega-toggle');
  item.addEventListener('pointerenter', (event) => {
    if (event.pointerType !== 'mouse' || !desktop.matches) return;
    clearTimeout(closeTimer);
    clearTimeout(openTimer);
    openTimer = window.setTimeout(() => openMegaItem(item), openMega ? 0 : 90);
  });
  item.addEventListener('pointerleave', (event) => {
    if (event.pointerType !== 'mouse' || !desktop.matches) return;
    clearTimeout(openTimer);
    closeTimer = window.setTimeout(() => closeMega(item), 220);
  });
  toggle.addEventListener('click', () => {
    if (item.classList.contains('is-open')) closeMega(item);
    else openMegaItem(item);
  });
  item.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && item.classList.contains('is-open')) {
      event.stopPropagation();
      closeMega(item, true);
    }
  });
  item.addEventListener('focusout', (event) => {
    if (!item.contains(/** @type {Node} */ (event.relatedTarget))) closeMega(item);
  });
}

desktop.addEventListener('change', () => closeMega());

/* ---------- Mega search ---------- */
class LamourSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-lc-search-input]');
    this.panel = this.querySelector('[data-lc-search-panel]');
    this.defaultView = this.querySelector('[data-lc-search-default]');
    this.live = this.querySelector('[data-lc-search-live]');
    this.clear = this.querySelector('[data-lc-search-clear]');
    this.cache = new Map();
    this.timer = 0;
    this.controller = null;
    this.money = new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: this.dataset.currency || 'USD',
    });

    this.input.addEventListener('focus', () => this.open());
    this.input.addEventListener('input', () => this.onInput());
    this.input.addEventListener('keydown', (event) => this.onKeydown(event));
    this.clear.addEventListener('click', () => {
      this.input.value = '';
      this.onInput();
      this.input.focus();
    });
    document.addEventListener('pointerdown', (event) => {
      if (this.isOpen && !this.contains(/** @type {Node} */ (event.target))) this.close();
    });
    this.addEventListener('focusout', (event) => {
      if (!this.contains(/** @type {Node} */ (event.relatedTarget))) this.close();
    });
    scrim?.addEventListener('click', () => this.close());
  }

  get isOpen() {
    return !this.panel.hidden;
  }

  open() {
    if (this.isOpen) return;
    closeMega();
    this.panel.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
    this.classList.add('is-open');
    openLayers.add('search');
    syncScrim();
  }

  close() {
    if (!this.isOpen) return;
    this.panel.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.classList.remove('is-open');
    openLayers.delete('search');
    syncScrim();
  }

  onInput() {
    const term = this.input.value.trim();
    this.clear.hidden = term.length === 0;
    this.open();
    clearTimeout(this.timer);
    if (term.length < 2) {
      this.controller?.abort();
      this.live.hidden = true;
      this.defaultView.hidden = false;
      return;
    }
    this.timer = window.setTimeout(() => this.search(term), 180);
  }

  async search(term) {
    this.defaultView.hidden = true;
    this.live.hidden = false;
    if (this.cache.has(term)) {
      this.render(term, this.cache.get(term));
      return;
    }
    this.live.innerHTML = `<p class="lc-sp-status">${this.escape(this.dataset.labelLoading)}</p>`;
    this.controller?.abort();
    this.controller = new AbortController();
    const params = new URLSearchParams({
      q: term,
      'resources[type]': 'product,collection,page,query',
      'resources[limit]': this.dataset.limit || '6',
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': 'last',
      'resources[options][fields]': 'title,product_type,variants.title,vendor,tag',
    });
    try {
      const response = await fetch(`${this.dataset.root}search/suggest.json?${params}`, { signal: this.controller.signal });
      const data = await response.json();
      const results = data?.resources?.results ?? {};
      this.cache.set(term, results);
      if (this.input.value.trim() === term) this.render(term, results);
    } catch (error) {
      if (error.name !== 'AbortError') this.live.innerHTML = '';
    }
  }

  escape(value = '') {
    return String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  }

  price(product) {
    const min = Number.parseFloat(product.price_min ?? product.price);
    const max = Number.parseFloat(product.price_max ?? product.price);
    const from = max > min ? `${this.escape(this.dataset.labelFrom)} ` : '';
    return Number.isFinite(min) ? from + this.money.format(min) : '';
  }

  image(url, width) {
    if (!url) return '';
    return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
  }

  render(term, results) {
    const hidden = (this.dataset.hidden || '').toLowerCase().split(',').map((w) => w.trim()).filter(Boolean);
    const visible = (text = '') => !hidden.some((w) => String(text).toLowerCase().includes(w));
    const products = (results.products ?? []).filter((p) => visible(`${p.title} ${p.vendor}`));
    const collections = (results.collections ?? []).filter((c) => visible(c.title));
    const pages = results.pages ?? [];
    const queries = (results.queries ?? []).filter((q) => q.text.toLowerCase() !== term.toLowerCase() && visible(q.text));
    const searchUrl = `${this.dataset.root}search?q=${encodeURIComponent(term)}&type=product&options%5Bprefix%5D=last`;
    const safeTerm = this.escape(term);

    if (!products.length && !collections.length && !pages.length && !queries.length) {
      this.live.innerHTML = `<div class="lc-sp-empty"><p>${this.escape(this.dataset.labelEmpty).replace('[terms]', safeTerm)}</p></div>`;
      return;
    }

    const side = [];
    if (queries.length) {
      side.push(`<p class="lc-sp-heading">${this.escape(this.dataset.labelSuggestions)}</p><ul class="lc-sp-chips" role="list">${queries
        .slice(0, 5)
        .map((q) => `<li><a class="lc-sp-chip" data-lc-option href="${q.url}">${q.styled_text || this.escape(q.text)}</a></li>`)
        .join('')}</ul>`);
    }
    if (collections.length) {
      side.push(`<p class="lc-sp-heading">${this.escape(this.dataset.labelCollections)}</p><ul class="lc-sp-links" role="list">${collections
        .slice(0, 5)
        .map((c) => `<li><a data-lc-option href="${c.url}"><span>${this.escape(c.title)}</span></a></li>`)
        .join('')}</ul>`);
    }
    if (pages.length) {
      side.push(`<p class="lc-sp-heading">${this.escape(this.dataset.labelPages)}</p><ul class="lc-sp-links" role="list">${pages
        .slice(0, 4)
        .map((p) => `<li><a data-lc-option href="${p.url}"><span>${this.escape(p.title)}</span></a></li>`)
        .join('')}</ul>`);
    }

    const cards = products
      .map((p) => {
        const img = p.featured_image?.url || p.image;
        return `<a class="lc-sp-product${p.available === false ? ' is-sold-out' : ''}" data-lc-option href="${p.url}"><span class="lc-sp-product-media">${
          img ? `<img src="${this.image(img, 200)}" alt="" loading="lazy" width="100" height="100">` : ''
        }</span><span class="lc-sp-product-text"><span class="lc-sp-product-title">${this.escape(p.title)}</span><span class="lc-sp-product-price">${this.price(p)}</span></span></a>`;
      })
      .join('');

    this.live.innerHTML = `<div class="lc-sp-grid">${side.length ? `<div class="lc-sp-col">${side.join('')}</div>` : ''}<div class="lc-sp-col lc-sp-col--products">${
      products.length
        ? `<div class="lc-sp-head-row"><p class="lc-sp-heading">${this.escape(this.dataset.labelProducts)}</p></div><div class="lc-sp-products">${cards}</div>`
        : ''
    }<a class="lc-sp-view-all" data-lc-option href="${searchUrl}">${this.escape(this.dataset.labelViewAll).replace('[terms]', safeTerm)}<span aria-hidden="true">&rarr;</span></a></div></div>`;
  }

  options() {
    const view = this.live.hidden ? this.defaultView : this.live;
    return [...view.querySelectorAll('[data-lc-option]')];
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const options = this.options();
    if (!options.length) return;
    event.preventDefault();
    this.open();
    const current = options.findIndex((el) => el.classList.contains('is-active'));
    const next = event.key === 'ArrowDown' ? (current + 1) % options.length : (current - 1 + options.length) % options.length;
    options.forEach((el, i) => {
      el.classList.toggle('is-active', i === next);
      if (!el.id) el.id = `lc-opt-${Math.random().toString(36).slice(2, 9)}`;
    });
    options[next].scrollIntoView({ block: 'nearest' });
    this.input.setAttribute('aria-activedescendant', options[next].id);
    this.activeOption = options[next];
  }
}

if (!customElements.get('lamour-search')) customElements.define('lamour-search', LamourSearch);

document.addEventListener('keydown', (event) => {
  if (event.key === 'Enter' && event.target instanceof HTMLInputElement && event.target.matches('[data-lc-search-input]')) {
    const active = event.target.closest('lamour-search')?.querySelector('.is-active[data-lc-option]');
    if (active instanceof HTMLAnchorElement) {
      event.preventDefault();
      window.location.href = active.href;
    }
  }
  if (event.key === 'Escape' && openMega) closeMega(openMega, true);
});

scrim?.addEventListener('click', () => closeMega());
